import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import ProposalComparisonView from './ProposalComparisonView';
import GuestProposalComparisonView from './GuestProposalComparisonView';
import { proposalComparisonService } from '../../services/api';

const ComparisonRouter = () => {
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [comparisonType, setComparisonType] = useState(null);
  
  const isGuest = searchParams.get('guest') === 'true';
  
  // Check if user is authenticated
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAuthenticated = !!(token && user && user.id);

  useEffect(() => {
    const checkComparisonAccess = async () => {
      try {
        setIsLoading(true);
        
        // If explicitly marked as guest, try guest access
        if (isGuest) {
          setComparisonType('guest');
          setIsLoading(false);
          return;
        }
        
        // If user is authenticated, use authenticated access
        if (isAuthenticated) {
          setComparisonType('authenticated');
          setIsLoading(false);
          return;
        }
        
        // If not authenticated and not explicitly guest, we need to determine the comparison type
        // Try to fetch the comparison to see if it's a guest comparison or requires authentication
        try {
          // First try guest access with any available guest session ID
          const guestSessionId = localStorage.getItem('guestSessionId') || sessionStorage.getItem('guestSessionId');
          if (guestSessionId) {
            const response = await proposalComparisonService.getComparisonById(id, true);
            if (response.data && response.data.status === 'success') {
              setComparisonType('guest');
              setIsLoading(false);
              return;
            }
          }
        } catch (guestError) {
          // Guest access failed, this might be a migrated comparison
          console.log('Guest access failed, redirecting to login');
        }
        
        // If we reach here, it's likely a migrated comparison that requires authentication
        navigate('/homeowner/login', { 
          state: { 
            returnTo: `/homeowner/comparisons/${id}`,
            message: 'Please log in to access this comparison' 
          }
        });
        
      } catch (error) {
        console.error('Error checking comparison access:', error);
        setComparisonType('guest'); // Fallback to guest view
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      checkComparisonAccess();
    } else {
      setIsLoading(false);
    }
  }, [id, isGuest, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #f3f3f3', 
            borderTop: '4px solid #0077cc', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }}></div>
          <p>Loading comparison...</p>
        </div>
      </>
    );
  }
  
  // Show guest view if explicitly requested or if determined to be guest comparison
  if (comparisonType === 'guest') {
    return <GuestProposalComparisonView />;
  }
  
  // Show authenticated user view
  return <ProposalComparisonView />;
};

export default ComparisonRouter; 